/**
 * Central constants for the Online Visa module.
 *
 * Keep all module-specific routes / opids / action codes here so that when the
 * folder is copied to another client codebase, only this single file needs to
 * be audited for environment-specific differences.
 */

export const ONLINE_VISA_ROUTES = {
  LANDING: '/online-visa',
  OPTIONS: '/online-visa/options',
  BOOKING: '/online-visa/booking',
  BOOKING_DETAILS: '/online-visa/booking-details/:transactionId',
  INVOICE: '/online-visa/invoice/:transactionId',
};

export const ONLINE_VISA_API = {
  OPID: 'MANAGEIWEENVISA',
  // Booking uses a different opid, confirmed by the provided cURL.
  ACTIONS: {
    GET_COUNTRIES: 'GETCOUNTRIESLIST',
    VISA_SEARCH: 'VISASEARCH',
    FETCH_VISA_DETAILS: 'FETCHVISADETAILS',
    GET_REQUIRED_DOCUMENTS: 'GETREQUIREDDOCUMENTS',
    PERFORM_VISA_BOOKING: 'PERFOMVISABOOKING',
    GET_VISA_APPLICATION: 'GETVISAAPPLICATIONBYID',
    GET_ALL_VISA_APPLICATIONS: 'GETALLVISAAPPLICATIONS',
  },
};

// Pax-type codes used in the booking request payload.
export const VISA_PAX_TYPES = {
  ADULT: 'ADT',
  CHILD: 'CHD',
  INFANT: 'INF',
};

export const PASSENGER_TITLE_OPTIONS = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Mstr', label: 'Mstr' },
];

export const OCCUPATION_OPTIONS = [
  { value: 'Student', label: 'Student' },
  { value: 'Employed', label: 'Employed' },
  { value: 'Self Employed', label: 'Self Employed' },
  { value: 'Business', label: 'Business' },
  { value: 'Retired', label: 'Retired' },
  { value: 'Homemaker', label: 'Homemaker' },
  { value: 'Other', label: 'Other' },
];

export const DEFAULT_NATIONALITY = {
  countryName: 'India',
  countryCode: 'IN',
};

export const PAX_CONFIG = {
  MIN_ADULTS: 1,
  MAX_ADULTS: 9,
  MIN_CHILDREN: 0,
  MAX_CHILDREN: 8,
  MIN_INFANTS: 0,
  MAX_INFANTS: 4,
  CHILD_AGE_MIN: 2,
  CHILD_AGE_MAX: 12,
  INFANT_AGE_MIN: 0,
  INFANT_AGE_MAX: 1,
};

// Selectable age ranges for child / infant dropdowns in the search form.
export const CHILD_AGE_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i + 2),
  label: `${i + 2} Yrs`,
}));

export const INFANT_AGE_OPTIONS = [
  { value: '0', label: '0 Yrs' },
  { value: '1', label: '1 Yr' },
];

export const VISA_TYPE_LABELS = {
  Sticker: 'Sticker',
  'e-visa': 'e-Visa',
  eVisa: 'e-Visa',
};

export const ENTRY_LABELS = {
  SINGLE: 'Single Entry',
  MULTIPLE: 'Multiple Entry',
  DOUBLE: 'Double Entry',
};

// Purpose options surfaced in the search form's Visa Type dropdown.
// These map to the `purpose` field returned by VISASEARCH results.
export const VISA_PURPOSE_OPTIONS = [
  { value: '', label: 'All Visa Types' },
  { value: 'TOURIST', label: 'Tourist Visa' },
  { value: 'BUSINESS', label: 'Business Visa' },
  { value: 'TRANSIT', label: 'Transit Visa' },
  { value: 'STUDENT', label: 'Student Visa' },
];

// Standard document checklist surfaced on the Options page sidebar.
// The real list comes from the visa detail response, but we show this default
// until a specific visa is selected.
export const DEFAULT_REQUIRED_DOCUMENTS = [
  { key: 'photograph', label: 'Photograph' },
  { key: 'passport', label: 'Passport' },
  { key: 'pan', label: 'Pan card' },
  { key: 'flightTicket', label: 'Flight Ticket' },
  { key: 'accommodation', label: 'Accommodation' },
  { key: 'coverLetter', label: 'Cover Letter' },
  { key: 'bankStatement', label: 'Bank Statement' },
];
